import { computed, type ComputedRef, type Ref } from 'vue';
import { CODES, UseLiveQueryConfigV2, useLiveQueryV2 } from '@libs/use';
import type { Instrument } from '@libs/api-reference';
import { omitBy } from '@libs/utils';

import {
  transformBidAskChange,
  transformClosingPriceChange,
  transformTradePriceChange,
  transformSymbolTradingStateChange,
  transformIndividualFirm,
  transformNav,
} from '../utils/transforms';
import {
  SYMBOL_CHANNEL_PREFIX,
  SYMBOL_BID_ASK_CHANNEL_PREFIX,
  SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX,
  SYMBOL_TRADE_CHANNEL_PREFIX,
  SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX,
  SYMBOL_INDIVIDUAL_PREFIX,
  SYMBOL_NAV_PREFIX,
  SYMBOL_QUERY_KEYS,
} from '../constants';
import * as API from '../requests';

type UseLiveSymbolQueryReturnType = {
  symbol: ComputedRef<Instrument>;
  getSymbol: ReturnType<typeof useLiveQueryV2>['execute'];
  symbolStatus: ReturnType<typeof useLiveQueryV2>['status'];
  isLoadingSymbol: Ref<boolean>;
  isRevalidatingSymbol: Ref<boolean>;
  isErrorSymbol: Ref<boolean>;
};

export default function useLiveSymbolQuery(
  id: Ref<string>,
  {
    immediate = true,
    staleTime = 1000,
    unsubscribe = true,
  }: Pick<
    UseLiveQueryConfigV2<Instrument>,
    'immediate' | 'unsubscribe' | 'staleTime'
  > = {}
): UseLiveSymbolQueryReturnType {
  const {
    result,
    isPending: isLoadingSymbol,
    isRevalidating: isRevalidatingSymbol,
    isError: isErrorSymbol,
    execute: getSymbol,
    status: symbolStatus,
  } = useLiveQueryV2<Instrument>({
    fetcher: () => API.getSymbol(id.value),
    immediate,
    unsubscribe,
    staleTime,
    key: SYMBOL_QUERY_KEYS.symbol(id),
    getChannels() {
      return [
        `${SYMBOL_CHANNEL_PREFIX}${id.value}`,
        `${SYMBOL_BID_ASK_CHANNEL_PREFIX}${id.value}`,
        `${SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX}${id.value}`,
        `${SYMBOL_TRADE_CHANNEL_PREFIX}${id.value}`,
        `${SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX}${id.value}`,
        `${SYMBOL_INDIVIDUAL_PREFIX}${id.value}`,
        `${SYMBOL_NAV_PREFIX}${id.value}`,
      ];
    },
    onUpdate(signal) {
      const actions = {
        [CODES.SymbolTradePriceChangeSignal]: transformTradePriceChange,
        [CODES.TseInstrumentClosingPriceChangeSignal]:
          transformClosingPriceChange,
        [CODES.SymbolBidAskChangeSignal]: transformBidAskChange,
        [CODES.MdpIndividualSignal]: transformIndividualFirm,
        [CODES.SymbolStateChangeSignal]: transformSymbolTradingStateChange,
        [CODES.MdpNavSignal]: transformNav,
      };

      if (actions[signal.code]) {
        const data = omitBy(
          actions[signal.code](signal.data),
          (v) => v === null || v === undefined
        );
        if (result.value) {
          if (signal.code === CODES.SymbolBidAskChangeSignal) {
            (data as ReturnType<typeof transformBidAskChange>).bidAsk.map(
              (item) => {
                if (result.value) {
                  const index = result.value.data.bidAsk.findIndex(
                    (s) => s.rowPlace === item.rowPlace
                  );

                  if (index > -1) {
                    Object.assign(result.value.data.bidAsk[index], item);
                  }
                }
              }
            );
          } else {
            Object.assign(result.value.data, data);
          }
        }
      }
    },
  });

  const symbol = computed<Instrument | any>(() => result.value?.data || {});

  return {
    symbol,
    getSymbol,
    isLoadingSymbol,
    isErrorSymbol,
    isRevalidatingSymbol,
    symbolStatus,
  };
}
